import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Bookmark, FileText, Trash2 } from 'lucide-react';
import { useBookmarks, useProgress, useStreak } from '../hooks/useLocal';
import { Card } from '../components/ui/card';
import { Flame, Check, TrendingUp } from 'lucide-react';
import { sampleNotes } from '../mock';

export default function BookmarksPage() {
  const navigate = useNavigate();
  const { bookmarks, remove } = useBookmarks();
  const { readCount } = useProgress();
  const { streak } = useStreak();

  const totalNotes = Object.values(sampleNotes).reduce((s, arr) => s + arr.length, 0);
  const readPercent = totalNotes ? Math.round((readCount / totalNotes) * 100) : 0;

  const openBookmark = (b) => {
    // Navigate to the notes list for that category
    navigate(`/notes/${b.category}`);
  };

  return (
    <div className="px-4 pt-4 space-y-4">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-emerald-600 font-medium">
        <ArrowLeft size={16} /> ମୁଖ୍ୟ ପେଜ କୁ ଫେରନ୍ତୁ
      </Link>

      <div className="rounded-3xl p-5 text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)' }}>
        <Bookmark size={26} />
        <h1 className="text-xl font-extrabold mt-2">ମୋର ଚିହ୍ନିତ ପାଠ</h1>
        <p className="text-xs text-white/90 mt-1">My Bookmarks & Progress</p>
      </div>

      {/* Progress cards */}
      <div className="grid grid-cols-3 gap-2.5">
        <Card className="rounded-2xl p-3 border-gray-200 text-center">
          <Flame size={18} className="mx-auto text-orange-500" />
          <div className="text-lg font-extrabold text-gray-900 mt-1">{streak}</div>
          <div className="text-[10px] text-gray-500 leading-tight">ଦିନ ଷ୍ଟ୍ରିକ<br/>Day Streak</div>
        </Card>
        <Card className="rounded-2xl p-3 border-gray-200 text-center">
          <Check size={18} className="mx-auto text-emerald-500" />
          <div className="text-lg font-extrabold text-gray-900 mt-1">{readCount}</div>
          <div className="text-[10px] text-gray-500 leading-tight">ପଠିତ<br/>Notes Read</div>
        </Card>
        <Card className="rounded-2xl p-3 border-gray-200 text-center">
          <TrendingUp size={18} className="mx-auto text-blue-500" />
          <div className="text-lg font-extrabold text-gray-900 mt-1">{readPercent}%</div>
          <div className="text-[10px] text-gray-500 leading-tight">ଆଗଦାନ<br/>Progress</div>
        </Card>
      </div>

      {/* Progress bar */}
      <Card className="rounded-2xl p-4 border-gray-200">
        <div className="flex items-center justify-between text-sm font-semibold text-gray-800">
          <span>ସାମଗ୍ରିକ ଆଗଦାନ</span>
          <span className="text-emerald-600">{readCount} / {totalNotes}</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full mt-2 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all" style={{ width: `${readPercent}%` }} />
        </div>
      </Card>

      {/* Bookmarks List */}
      <div>
        <h3 className="text-base font-extrabold text-gray-900">ଚିହ୍ନିତ ଅଧ୍ୟାୟ · Saved</h3>
        {bookmarks.length === 0 ? (
          <div className="text-center py-8 text-sm text-gray-500">
            <Bookmark size={32} className="mx-auto text-gray-300 mb-2" />
            କୌଣସି ବୁକମାର୍କ ନାହିଁ · ନୋଟ୍ସ ପଢ଼ି ଚିହ୍ନ କରନ୍ତୁ
          </div>
        ) : (
          <div className="space-y-2 mt-3">
            {bookmarks.map((b) => (
              <Card key={b.id} className="rounded-2xl p-3 border-gray-200 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
                  <FileText size={17} />
                </div>
                <button onClick={() => openBookmark(b)} className="flex-1 text-left min-w-0">
                  <div className="font-semibold text-sm text-gray-900 truncate">{b.title}</div>
                  <div className="text-[11px] text-gray-500">{b.subject} · {b.category}</div>
                </button>
                <button onClick={() => remove(b.id)} className="w-8 h-8 rounded-lg text-red-500 hover:bg-red-50 flex items-center justify-center">
                  <Trash2 size={16} />
                </button>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
